import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureException } from "@sentry/react";

import { mapResumedMessages, resumeNexaThread } from "./api";
import {
  ATTACHMENT_ACCEPT,
  composeMessageWithAttachments,
  describeSize,
  MAX_ATTACHMENTS,
  readAttachments,
  type NexaAttachment,
  type RejectedAttachment,
} from "./attachments";
import { NEXA_DISCLAIMER, NEXA_NAME, type NexaLayout } from "./constants";
import LayoutMenu from "./components/LayoutMenu";
import NexaEmptyState from "./components/NexaEmptyState";
import NexaMark from "./components/NexaMark";
import TurnOutcomeView from "./components/TurnOutcomeView";
import TurnProgress from "./components/TurnProgress";
import { useNexaTurn } from "./useNexaTurn";

interface NexaPanelProps {
  threadId: string | null;
  userName?: string;
  contextLabel?: string;
  activeLayout?: NexaLayout;
  onLayoutChange?: (layout: NexaLayout) => void;
  onOpenHistory?: () => void;
  onClose?: () => void;
  onNewChat?: () => void;
  onThreadUnavailable?: () => void;
  onSessionExpired?: () => void;
  onCreateThread?: () => Promise<string>;
  onTurnComplete?: () => void;
  variant?: "panel" | "workspace";
  threadError?: boolean;
  onRetryThread?: () => void;
}

const HeaderButton = ({
  title,
  onClick,
  active,
  wide,
  children,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-haspopup={wide ? "menu" : undefined}
    aria-expanded={wide ? !!active : undefined}
    onClick={onClick}
    className={`tw-flex tw-h-[30px] tw-items-center tw-justify-center tw-gap-0.5 tw-rounded-lg tw-border tw-text-gray-600 hover:tw-bg-gray-100 ${
      wide ? "tw-px-1.5" : "tw-w-[30px]"
    } ${active ? "tw-border-[#C7CDFB] tw-bg-[#EEF0FE] tw-text-[#3730A3]" : "tw-border-transparent"}`}
  >
    {children}
  </button>
);

const NexaPanel = ({
  threadId,
  userName,
  contextLabel,
  activeLayout = "floating",
  onLayoutChange,
  onOpenHistory,
  onClose,
  onNewChat,
  onThreadUnavailable,
  onSessionExpired,
  threadError,
  onRetryThread,
  onCreateThread,
  onTurnComplete,
  variant = "panel",
}: NexaPanelProps) => {
  const [draft, setDraft] = useState("");
  const [turnStartedAt, setTurnStartedAt] = useState(0);
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  const [attachments, setAttachments] = useState<NexaAttachment[]>([]);
  const [rejected, setRejected] = useState<RejectedAttachment[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { status, phases, messages, closeReason, needsResume, lastError, hasPending, send, cancelPending, setMessages, clearNeedsResume } = useNexaTurn({ threadId, onTurnComplete });
  const [creatingThread, setCreatingThread] = useState(false);
  const [resuming, setResuming] = useState(() => !!threadId);
  const creatingRef = useRef(false);

  const lastUserMessage = useMemo(() => [...messages].reverse().find((m) => m.role === "user"), [messages]);

  useEffect(() => {
    if (status === "running" && !turnStartedAt) setTurnStartedAt(Date.now());
    if (status !== "running" && turnStartedAt) setTurnStartedAt(0);
  }, [status, turnStartedAt]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, phases]);

  useEffect(() => {
    if (!layoutMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setLayoutMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLayoutMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [layoutMenuOpen]);

  useEffect(() => {
    if (closeReason === "forbidden") onThreadUnavailable?.();
    if (closeReason === "unauthenticated") onSessionExpired?.();
  }, [closeReason, onThreadUnavailable, onSessionExpired]);

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    setResuming(true);
    resumeNexaThread(threadId)
      .then((data) => {
        if (cancelled) return;
        setMessages((prev) => {
          const resumed = mapResumedMessages(data.messages);
          return resumed.length === 0 && prev.length > 0 ? prev : resumed;
        });
        clearNeedsResume();
      })
      .catch((err) => {
        captureException(err);
        if (!cancelled) clearNeedsResume();
      })
      .finally(() => {
        if (!cancelled) setResuming(false);
      });
    return () => {
      cancelled = true;
    };
  }, [threadId, needsResume, clearNeedsResume, setMessages]);

  const hasContent = draft.trim().length > 0 || attachments.length > 0;
  const busy = status === "running" || creatingThread || hasPending;
  const canSend = hasContent && !busy && status !== "closed";

  const pickFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const { accepted, rejected: refused } = await readAttachments(Array.from(files), attachments.length);
      if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
      setRejected(refused);
    },
    [attachments.length]
  );

  const removeAttachment = useCallback((id: string) => setAttachments((prev) => prev.filter((a) => a.id !== id)), []);

  const submit = useCallback(
    async (text?: string) => {
      const typed = (text ?? draft).trim();
      if ((!typed && attachments.length === 0) || status === "running" || status === "closed" || hasPending || creatingRef.current) return;

      const body = composeMessageWithAttachments(typed, attachments);
      const keptDraft = draft;
      const keptAttachments = attachments;

      send(body);
      setDraft("");
      setAttachments([]);
      setRejected([]);

      if (threadId || !onCreateThread) return;

      creatingRef.current = true;
      setCreatingThread(true);
      try {
        await onCreateThread();
      } catch (err) {
        captureException(err);
        cancelPending();
        setDraft(keptDraft);
        setAttachments(keptAttachments);
      } finally {
        creatingRef.current = false;
        setCreatingThread(false);
      }
    },
    [draft, status, hasPending, send, attachments, threadId, onCreateThread, cancelPending]
  );

  const retryLast = useCallback(() => {
    if (status !== "idle" || lastUserMessage?.role !== "user") return;
    send(lastUserMessage.text);
  }, [status, lastUserMessage, send]);

  const showEmptyState = messages.length === 0 && status !== "closed" && !resuming;
  const isWorkspace = variant === "workspace";

  return (
    <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-bg-white">
      <header ref={headerRef} className="tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#F0F0F7] tw-px-3 tw-py-2">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          {onOpenHistory && (
            <HeaderButton title="Chat history" onClick={onOpenHistory}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2.6 4.2h10.8M2.6 8h10.8M2.6 11.8h10.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </HeaderButton>
          )}
          <NexaMark />
          <span className="tw-truncate tw-text-label-md tw-font-bold tw-text-[#1E1B4B]">{NEXA_NAME}</span>
          {isWorkspace && (
            <span className="tw-shrink-0 tw-rounded-md tw-bg-[#EEF0FE] tw-px-2 tw-py-0.5 tw-text-label-xs tw-font-medium tw-text-[#3730A3]">Workspace</span>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-gap-1">
          {onNewChat && (
            <HeaderButton title="New chat" onClick={onNewChat}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M11.4 2.6 13.4 4.6 5.6 12.4l-2.6.6.6-2.6Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
            </HeaderButton>
          )}
          {onLayoutChange && (
            <HeaderButton title="Window style — floating, sidebar or full screen" active={layoutMenuOpen} onClick={() => setLayoutMenuOpen((v) => !v)} wide>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect x="2.4" y="3" width="11.2" height="10" rx="1.6" stroke="currentColor" strokeWidth="1.3" />
                <path d="M2.4 6h11.2" stroke="currentColor" strokeWidth="1.3" />
              </svg>
              <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 4.5 6 7.5l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </HeaderButton>
          )}
          {onClose && (
            <HeaderButton title="Close" onClick={onClose}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </HeaderButton>
          )}
        </div>

        {layoutMenuOpen && onLayoutChange && (
          <LayoutMenu
            active={activeLayout}
            onPick={(layout) => {
              setLayoutMenuOpen(false);
              onLayoutChange(layout);
            }}
          />
        )}
      </header>

      <div ref={scrollRef} className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4">
        <div className={isWorkspace ? "tw-mx-auto tw-w-full tw-max-w-[720px]" : ""}>
        {resuming && messages.length === 0 && (
          <div className="tw-px-1 tw-py-3 tw-text-label-sm tw-text-gray-500">Loading conversation…</div>
        )}

        {showEmptyState && (
          <NexaEmptyState
            variant={variant}
            userName={userName}
            contextLabel={contextLabel}
            disabled={busy}
            onPrompt={(prompt) => void submit(prompt)}
            onIntent={(prompt) => {
              setDraft(prompt);
              composerRef.current?.focus();
            }}
          />
        )}

        {messages.map((message) =>
          message.role === "user" ? (
            <div key={message.id} className="tw-mb-4 tw-flex tw-justify-end">
              <div className="tw-max-w-[80%] tw-rounded-xl tw-bg-[#EEF0FE] tw-px-3.5 tw-py-2.5 tw-text-label-md tw-leading-relaxed tw-text-[#1E1B4B]">{message.text}</div>
            </div>
          ) : (
            <div key={message.id} className="tw-mb-5">
              <TurnOutcomeView outcome={message.outcome} onRetry={retryLast} disabled={status !== "idle"} onAnswerField={(value) => void submit(value)} />
            </div>
          )
        )}

        {status === "running" && <TurnProgress phases={phases} startedAt={turnStartedAt || Date.now()} />}

        {closeReason === "unauthenticated" && (
          <div className="tw-rounded-xl tw-border tw-border-error-200 tw-bg-error-50 tw-p-3">
            <div className="tw-text-label-md tw-font-semibold tw-text-error-600">Your session ended</div>
            <div className="tw-mt-1 tw-text-label-sm tw-leading-relaxed tw-text-gray-600">
              GTC allows one active session, so signing in elsewhere signs you out here. Sign in again to carry on.
            </div>
          </div>
        )}

        {closeReason === "forbidden" && (
          <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-3">
            <div className="tw-text-label-md tw-font-semibold tw-text-gray-700">This conversation isn't available</div>
            <div className="tw-mt-1 tw-text-label-sm tw-leading-relaxed tw-text-gray-600">Head back to your conversations and pick another.</div>
          </div>
        )}

        {lastError && <div className="tw-mt-2 tw-text-label-xs tw-text-gray-500">{lastError}</div>}
        </div>
      </div>

      <div className="tw-shrink-0 tw-px-3 tw-pb-3">
        <div className={isWorkspace ? "tw-mx-auto tw-w-full tw-max-w-[720px]" : ""}>
        {threadError && (
          <div className="tw-mb-2 tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-border tw-border-error-200 tw-bg-error-50 tw-px-3 tw-py-2">
            <div className="tw-min-w-0 tw-flex-1">
              <div className="tw-text-label-sm tw-font-semibold tw-text-error-600">Not connected to Nexa AI</div>
              <div className="tw-mt-0.5 tw-text-label-xs tw-leading-relaxed tw-text-gray-600">
                The assistant service didn't respond, so nothing can be sent yet.
              </div>
            </div>
            {onRetryThread && (
              <button
                type="button"
                onClick={onRetryThread}
                className="tw-shrink-0 tw-rounded-lg tw-border tw-border-error-200 tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-label-xs tw-font-medium tw-text-error-600 hover:tw-bg-error-50"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {!threadError && status === "connecting" && (
          <div className="tw-mb-2 tw-px-1 tw-text-label-xs tw-text-gray-500">Connecting to Nexa AI…</div>
        )}

        {contextLabel && (
          <div className="tw-mb-2 tw-rounded-lg tw-bg-[#F7F7FC] tw-px-3 tw-py-2 tw-text-label-xs tw-text-gray-600">
            Context: <span className="tw-font-medium tw-text-[#4F46E5]">{contextLabel}</span>
          </div>
        )}
        {attachments.length > 0 && (
          <div className="tw-mb-2 tw-flex tw-flex-wrap tw-gap-2">
            {attachments.map((file) => (
              <span key={file.id} className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-[#F4F3F9] tw-px-2.5 tw-py-1.5 tw-text-label-xs tw-text-[#3B3960]">
                <span className="tw-font-semibold tw-text-[#4F46E5]">{file.name.split(".").pop()?.toUpperCase()}</span>
                <span className="tw-max-w-[160px] tw-truncate">{file.name}</span>
                <span className="tw-text-gray-500">{describeSize(file.size)}</span>
                <button type="button" onClick={() => removeAttachment(file.id)} aria-label={`Remove ${file.name}`} className="tw-text-gray-500 hover:tw-text-gray-800">
                  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {rejected.length > 0 && (
          <div className="tw-mb-2 tw-rounded-lg tw-border tw-border-warning-200 tw-bg-warning-50 tw-px-2.5 tw-py-2">
            {rejected.map((file) => (
              <div key={file.name} className="tw-text-label-xs tw-leading-relaxed tw-text-[#92610A]">
                <span className="tw-font-medium">{file.name}</span> — {file.reason}
              </div>
            ))}
          </div>
        )}

        <div className="tw-rounded-xl tw-border tw-border-[#E2E2F0] tw-bg-white tw-p-3">
          <textarea
            ref={composerRef}
            rows={1}
            value={draft}
            disabled={status === "closed"}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void submit();
              }
            }}
            placeholder={busy ? "Nexa is working…" : isWorkspace ? "Ask Nexa anything about your trade data" : "Ask about classifications, duties or controls"}
            className="tw-max-h-32 tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-text-label-md tw-text-[#16141F] tw-outline-none placeholder:tw-text-gray-400"
          />
          <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="tw-hidden"
              onChange={(e) => {
                void pickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={status === "closed" || attachments.length >= MAX_ATTACHMENTS}
              title={`Attach a CSV, TSV, TXT, MD or JSON file (up to ${MAX_ATTACHMENTS})`}
              className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-[#E2E2F0] tw-px-2.5 tw-py-1.5 tw-text-label-xs tw-text-[#3B3960] hover:tw-bg-gray-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 3.4v9.2M3.4 8h9.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Attach file
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!canSend}
              title={
                threadError
                  ? "Not connected to Nexa AI"
                  : busy
                    ? "Wait for the current answer"
                    : status !== "idle"
                      ? "Connecting to Nexa AI…"
                      : "Send"
              }
              aria-label="Send"
              className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-lg tw-text-white disabled:tw-cursor-not-allowed"
              style={{ background: canSend ? "#4F46E5" : "#C7CDFB" }}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M8 13V3.5M3.8 7.7 8 3.5l4.2 4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
        <div className="tw-pt-2 tw-text-center tw-text-label-xs tw-text-gray-500">{NEXA_DISCLAIMER}</div>
        </div>
      </div>
    </div>
  );
};

export default NexaPanel;
